// Saisie d'une compétence transférée et de ses moyens (lot L6-A, D-274), par un compte ministériel. Le
// serveur relit le périmètre, distingue fictif et officiel, versionne et journalise ; la liste des
// compétences est indicative, à confirmer par l'administration.
import { COMPETENCES_TRANSFERABLES, NATURES_MOYEN, STATUTS_COMPETENCE, type CodeCompetence, type CompetenceTransferee, type NatureMoyen, type StatutCompetence } from '@cc/contrats';
import { Bouton, Champ, Selection, ZoneTexte } from '@cc/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useAction } from '../../commun/useAction';
import { Fenetre } from '../achats/Fenetre';
import { creerCompetence, INVALIDER_SUPERVISION, modifierCompetence } from './api';
import styles from '../achats/achats.module.css';

interface Moyen {
  nature: NatureMoyen;
  libelle: string;
  quantite: string;
  unite: string;
  montant: string;
}

const versMoyen = (m: Moyen) => ({
  nature: m.nature,
  libelle: m.libelle,
  quantite: m.quantite === '' ? null : Number(m.quantite),
  unite: m.unite === '' ? null : m.unite,
  montant_annuel: m.nature === 'financier' && m.montant !== '' ? Number.parseInt(m.montant, 10) : null,
});

export function FormulaireCompetence({ collectivites, existante, onFermer }: { collectivites: { id: string; nom: string }[]; existante?: CompetenceTransferee; onFermer: () => void }) {
  const [v, setV] = useState({
    collectivite_id: existante?.collectivite_id ?? collectivites[0]?.id ?? '',
    competence: (existante?.competence ?? 'etat_civil') as CodeCompetence,
    texte_reference: existante?.texte_reference ?? '',
    date_texte: existante?.date_texte ?? '',
    date_effet: existante?.date_effet ?? '',
    statut: (existante?.statut ?? 'en_vigueur') as StatutCompetence,
    observation: existante?.observation ?? '',
    motif: '',
  });
  const [moyens, setMoyens] = useState<Moyen[]>(
    (existante?.moyens ?? []).map((m) => ({ nature: m.nature, libelle: m.libelle, quantite: m.quantite === null ? '' : String(m.quantite), unite: m.unite ?? '', montant: m.montant_annuel ? String(m.montant_annuel) : '' })),
  );
  const maj = (cle: keyof typeof v, valeur: string) => setV((x) => ({ ...x, [cle]: valeur }));
  const majMoyen = (i: number, cle: keyof Moyen, valeur: string) => setMoyens((l) => l.map((m, j) => (j === i ? { ...m, [cle]: valeur } : m)));
  const corps = { competence: v.competence, texte_reference: v.texte_reference, date_texte: v.date_texte, date_effet: v.date_effet, observation: v.observation || null, moyens: moyens.map(versMoyen), motif: v.motif };
  const action = useAction({
    executer: () => (existante ? modifierCompetence(existante.competence_id, { ...corps, statut: v.statut }) : creerCompetence({ ...corps, collectivite_id: v.collectivite_id })),
    succes: (r) => `Compétence enregistrée (version ${r.version}).`,
    invalider: INVALIDER_SUPERVISION,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre={existante ? 'Modifier la compétence transférée' : 'Nouvelle compétence transférée'}
      description="Texte de référence, dates et moyens transférés. Chaque enregistrement crée une version datée, avec son motif."
      libelle="Enregistrer"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <div className={styles.grille}>
        <Selection libelle="Collectivité" options={collectivites.map((c) => ({ valeur: c.id, libelle: c.nom }))} value={v.collectivite_id} disabled={Boolean(existante)} onChange={(e) => maj('collectivite_id', e.target.value)} />
        <Selection libelle="Compétence" options={Object.entries(COMPETENCES_TRANSFERABLES).map(([valeur, libelle]) => ({ valeur, libelle }))} value={v.competence} onChange={(e) => maj('competence', e.target.value)} />
        <Champ libelle="Texte de référence" value={v.texte_reference} onChange={(e) => maj('texte_reference', e.target.value)} required />
        <Champ libelle="Date du texte" type="date" value={v.date_texte} onChange={(e) => maj('date_texte', e.target.value)} required />
        <Champ libelle="Date d'effet" type="date" value={v.date_effet} onChange={(e) => maj('date_effet', e.target.value)} required />
        {existante ? <Selection libelle="Statut" options={Object.entries(STATUTS_COMPETENCE).map(([valeur, libelle]) => ({ valeur, libelle }))} value={v.statut} onChange={(e) => maj('statut', e.target.value)} /> : null}
      </div>
      <ZoneTexte libelle="Observation (facultative)" value={v.observation} onChange={(e) => maj('observation', e.target.value)} />
      <h3>Moyens transférés</h3>
      {moyens.map((m, i) => (
        <div key={i} className={styles.grille} role="group" aria-label={`Moyen ${i + 1}`}>
          <Selection libelle={`Nature du moyen ${i + 1}`} options={Object.entries(NATURES_MOYEN).map(([valeur, libelle]) => ({ valeur, libelle }))} value={m.nature} onChange={(e) => majMoyen(i, 'nature', e.target.value)} />
          <Champ libelle={`Libellé du moyen ${i + 1}`} value={m.libelle} onChange={(e) => majMoyen(i, 'libelle', e.target.value)} />
          <Champ libelle={`Quantité du moyen ${i + 1}`} type="number" min={0} value={m.quantite} onChange={(e) => majMoyen(i, 'quantite', e.target.value)} />
          <Champ libelle={`Unité du moyen ${i + 1}`} value={m.unite} onChange={(e) => majMoyen(i, 'unite', e.target.value)} />
          {m.nature === 'financier' ? <Champ libelle={`Montant annuel du moyen ${i + 1} (F CFA)`} type="number" min={0} value={m.montant} onChange={(e) => majMoyen(i, 'montant', e.target.value)} /> : null}
          <Bouton variante="secondaire" icone={Trash2} onClick={() => setMoyens((l) => l.filter((_, j) => j !== i))}>Retirer le moyen {i + 1}</Bouton>
        </div>
      ))}
      <Bouton variante="secondaire" icone={Plus} onClick={() => setMoyens((l) => [...l, { nature: 'humain', libelle: '', quantite: '', unite: '', montant: '' }])}>Ajouter un moyen</Bouton>
      <Champ libelle="Motif de l'enregistrement" value={v.motif} onChange={(e) => maj('motif', e.target.value)} required />
    </Fenetre>
  );
}
